/**
 * Routing-API Service für Endpoints, die mit dem cargovibe-frontend geteilt werden
 * (Access-Key-Verifikation, getUserDetails).
 */

import {
  RedeemError,
  RedeemErrorCode,
  RedeemErrorResponseSchema,
  SubscriptionType,
  UserRole,
} from '@cargovibe/shared-types';
import http, { HttpError } from '@/lib/http';
import type { z } from 'zod';

export { RedeemError, RedeemErrorCode, SubscriptionType, UserRole };

export interface RoutingUserDetails {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
  role: UserRole;
  subscription: SubscriptionType;
  externalAuthId: string | null;
}

export async function fetchRoutingUserDetails(): Promise<RoutingUserDetails | null> {
  try {
    const data: { user: RoutingUserDetails } = await http.get('/getUserDetails');
    return data.user;
  } catch (error: unknown) {
    // Kein gültiger Zugang (z.B. 401/403/404) → kein Pro-User, kein Retry nötig.
    if (error instanceof HttpError) return null;
    throw error;
  }
}

export async function redeemAccessKey(key: string): Promise<RoutingUserDetails> {
  try {
    const data: { user: RoutingUserDetails } = await http.post('/redeemAccessKey', undefined, {
      key,
    });
    return data.user;
  } catch (error: unknown) {
    if (error instanceof HttpError) {
      const parsed: ReturnType<typeof RedeemErrorResponseSchema.safeParse> =
        RedeemErrorResponseSchema.safeParse(error.data);
      const body: z.infer<typeof RedeemErrorResponseSchema> | undefined = parsed.success
        ? parsed.data
        : undefined;
      throw new RedeemError(
        body?.error ?? 'Failed to redeem access key',
        body?.errorCode ?? RedeemErrorCode.Unknown
      );
    }
    throw error;
  }
}
